import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Upload } from 'lucide-react';
import {
  adminListNews,
  adminSaveNews,
  adminUploadImage,
  estimateReadTime,
  mediaUrl,
  slugify,
} from '../../api/news';

const emptyForm = () => ({
  id: null,
  title: '',
  slug: '',
  image: '',
  excerpt: '',
  date: '',
  readTime: '',
  published: true,
  publishedAt: new Date().toISOString(),
  body: [{ type: 'p', text: '' }],
});

const AdminNewsForm = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [slugManual, setSlugManual] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (isNew) return;
    let alive = true;
    adminListNews()
      .then((list) => {
        if (!alive) return;
        const found = list.find((n) => String(n.id) === String(id));
        if (!found) {
          setError('Matéria não encontrada.');
          return;
        }
        setForm({
          ...emptyForm(),
          ...found,
          body: found.body?.length ? found.body : [{ type: 'p', text: '' }],
        });
        setSlugManual(true);
      })
      .catch((e) => setError(e.message || 'Erro ao carregar.'))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id, isNew]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onTitle = (title) => {
    setForm((f) => ({
      ...f,
      title,
      slug: slugManual ? f.slug : slugify(title),
    }));
  };

  const updateBlock = (index, patch) => {
    setForm((f) => ({
      ...f,
      body: f.body.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    }));
  };

  const addBlock = (type) => {
    const block = type === 'image' ? { type: 'image', src: '', alt: '' } : { type: 'p', text: '' };
    setForm((f) => ({ ...f, body: [...f.body, block] }));
  };

  const removeBlock = (index) => {
    setForm((f) => ({ ...f, body: f.body.filter((_, i) => i !== index) }));
  };

  const upload = async (file, onUrl) => {
    if (!file) return;
    try {
      const url = await adminUploadImage(file);
      onUrl(url);
      setMsg('Imagem enviada.');
    } catch (e) {
      setError(e.message || 'Falha no upload.');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        readTime: form.readTime || estimateReadTime(form.body),
        publishedAt: form.publishedAt || new Date().toISOString(),
      };
      const saved = await adminSaveNews(payload);
      setMsg('Salvo com sucesso.');
      if (isNew) navigate(`/admin/news/${saved.id}`, { replace: true });
      else setForm((f) => ({ ...f, ...saved }));
    } catch (err) {
      setError(err.message || 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f3] text-[#555]">
        Carregando…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f3]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <header className="bg-[#0e0e0e] text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-white/70 hover:text-[#C99A36]">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <p className="text-[#C99A36] text-[10px] tracking-[0.35em] font-semibold">SETA ADMIN</p>
            <h1 className="text-lg font-bold">{isNew ? 'Nova matéria' : 'Editar matéria'}</h1>
          </div>
        </div>
      </header>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-6 py-10 space-y-5">
        {error && <p className="text-red-600 text-sm">{error}</p>}
        {msg && <p className="text-green-700 text-sm">{msg}</p>}

        <div>
          <label className="block text-xs text-[#666] mb-1">Título</label>
          <input
            className="w-full border px-3 py-2 text-sm bg-white"
            value={form.title}
            onChange={(e) => onTitle(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block text-xs text-[#666] mb-1">Slug</label>
          <input
            className="w-full border px-3 py-2 text-sm bg-white font-mono"
            value={form.slug}
            onChange={(e) => {
              setSlugManual(true);
              setField('slug', e.target.value);
            }}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-[#666] mb-1">Data (texto)</label>
            <input
              className="w-full border px-3 py-2 text-sm bg-white"
              value={form.date}
              onChange={(e) => setField('date', e.target.value)}
              placeholder="Julho de 2026"
            />
          </div>
          <div>
            <label className="block text-xs text-[#666] mb-1">Tempo de leitura</label>
            <input
              className="w-full border px-3 py-2 text-sm bg-white"
              value={form.readTime}
              onChange={(e) => setField('readTime', e.target.value)}
              placeholder="auto se vazio"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs text-[#666] mb-1">Excerpt</label>
          <textarea
            className="w-full border px-3 py-2 text-sm bg-white min-h-[80px]"
            value={form.excerpt}
            onChange={(e) => setField('excerpt', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs text-[#666] mb-1">Imagem de capa</label>
          <div className="flex gap-2 items-center">
            <input
              className="flex-1 border px-3 py-2 text-sm bg-white"
              value={form.image}
              onChange={(e) => setField('image', e.target.value)}
              placeholder="URL ou upload"
            />
            <label className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-3 py-2 text-xs cursor-pointer hover:bg-[#C99A36]">
              <Upload size={14} /> Upload
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0], (url) => setField('image', url))}
              />
            </label>
          </div>
          {form.image ? (
            <img src={mediaUrl(form.image)} alt="" className="mt-3 max-h-40 object-cover" />
          ) : null}
        </div>

        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.published}
            onChange={(e) => setField('published', e.target.checked)}
          />
          Publicada
        </label>

        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1a1a1a]">Corpo (blocos)</h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => addBlock('p')}
                className="inline-flex items-center gap-1 text-xs border px-2 py-1 bg-white hover:border-[#C99A36]"
              >
                <Plus size={12} /> Parágrafo
              </button>
              <button
                type="button"
                onClick={() => addBlock('image')}
                className="inline-flex items-center gap-1 text-xs border px-2 py-1 bg-white hover:border-[#C99A36]"
              >
                <Plus size={12} /> Imagem
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {form.body.map((block, i) => (
              <div key={i} className="bg-white border p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-[10px] tracking-widest text-[#888] uppercase">{block.type}</span>
                  <button type="button" onClick={() => removeBlock(i)} className="text-[#888] hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
                {block.type === 'p' ? (
                  <textarea
                    className="w-full border px-3 py-2 text-sm min-h-[100px]"
                    value={block.text || ''}
                    onChange={(e) => updateBlock(i, { text: e.target.value })}
                  />
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        className="flex-1 border px-3 py-2 text-sm"
                        value={block.src || ''}
                        onChange={(e) => updateBlock(i, { src: e.target.value })}
                        placeholder="src"
                      />
                      <label className="inline-flex items-center gap-1 bg-[#1a1a1a] text-white px-3 py-2 text-xs cursor-pointer">
                        <Upload size={12} />
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          onChange={(e) =>
                            upload(e.target.files?.[0], (url) => updateBlock(i, { src: url }))
                          }
                        />
                      </label>
                    </div>
                    <input
                      className="w-full border px-3 py-2 text-sm"
                      value={block.alt || ''}
                      onChange={(e) => updateBlock(i, { alt: e.target.value })}
                      placeholder="alt / legenda"
                    />
                    {block.src ? (
                      <img src={mediaUrl(block.src)} alt="" className="max-h-40 object-cover" />
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full md:w-auto px-8 py-3 bg-[#1a1a1a] text-white text-[12px] tracking-[0.2em] font-semibold hover:bg-[#C99A36] disabled:opacity-60"
        >
          {saving ? 'SALVANDO…' : 'SALVAR'}
        </button>
      </form>
    </div>
  );
};

export default AdminNewsForm;
